<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { defaultProps, defaultFunction } from '@/components/_parials/buttonCore'

export default defineComponent({
  name: 'ButtonLpCta',

  props: {
    ...defaultProps,

    /**
     * クリックした際に発行する emit の名称
     * @description ルート要素の class（o-action__cta）に合わせた名前付きの click を既定とする
     */
    emitName: {
      type: String,
      default: 'click:cta',
    },

    size: {
      type: String as PropType<'small' | 'normal' | 'large'>,
      default: 'normal',
    },

    fontSize: {
      type: String as PropType<'xSmall' | 'small' | 'normal' | 'large' | 'xLarge' | 'xxLarge'>,
      default: 'normal',
    },

    /**
     * ボタンにエフェクトアニメーションさせるかどうか
     */
    animate: {
      type: Boolean,
      default: false,
    },

    caption: {
      type: String,
      default: '',
    },

    /**
    * Form Submit ボタンかどうか
    * @description isLink が true の場合は本設定は無視される
    * @description リンクではなく、且つ submit も false だった場合は、ただの button 要素になる
    */
    isSubmit: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const {
      // data
      wrapper,

      // computed
      hasSlot,

      // methods
      runEvent,
    } = defaultFunction(props, emit)

    const buttonType = computed(() => {
      return props.isSubmit ? 'submit' : 'button'
    })

    return {
      // data
      wrapper,

      // computed
      hasSlot,
      buttonType,

      // methods
      runEvent,
    }
  }
})
</script>

<template>
  <div
    v-if="hasSlot"
    ref="wrapper"
    :class="[
      'o-action__cta',
      { 'is-bold': isFontBold },
      { 'is-shadow': hasShadow },
      { 'is-narrowMargin': narrowMargin },
      { 'is-animate': animate },
    ]"
    :data-font-size="fontSize"
    :data-size="size">
    <p
      v-if="caption"
      class="o-action__cta--caption"
      v-html="caption" />

    <a
      v-if="isLink"
      class="o-action__cta--item"
      :href="href"
      :target="target">
      <span class="o-action__cta--label">
        <slot />
      </span>
    </a>
    <button
      v-else
      class="o-action__cta--item"
      :type="buttonType"
      :name="name"
      :disabled="disabled"
      @click="runEvent">
      <span class="o-action__cta--label">
        <slot />
      </span>
    </button>
  <!-- /.o-action__cta --></div>
</template>

<style lang="sass" scoped>
.o-action__cta
  $root: &
  margin: 2.5em auto 0

  &:first-child
    margin-top: 0

  & &--item
    width: 100%
    display: flex
    flex-direction: column
    justify-content: center
    align-items: center
    position: relative
    overflow: hidden
    line-height: 1.5
    color: $font-white
    font-weight: bold
    text-decoration: none
    padding:
      left: changeSizePx2Em(20, $font-small-px)
      right: changeSizePx2Em(20, $font-small-px)
    background-color: $bg-x-dark-orange
    border-radius: 100vh
    box-shadow: 0 4px 4px 0 rgba($bg-lp-orange, 0.25)
    +transition

    +mouseover
      background-color: $bg-xx-dark-orange

  & &--caption
    color: #2c3e50
    text-align: center
    margin-bottom: 0.2em

  &.is-animate
    #{$root}--item
      &::before
        content: ''
        width: 100%
        height: 100%
        display: block
        position: absolute
        top: 0
        left: -100%
        background-image: linear-gradient(130deg, rgba($bg-white, 0) 0%, rgba($bg-white, 0.5) 80%, rgba($bg-white, 100) 81%, rgba($bg-white, 0) 100%)
        animation: shine 7s infinite

  &[data-size='small']
    #{$root}--item
      padding:
        top: 0.7em
        bottom: 0.7em

    #{$root}--caption
      font-size: $font-small

  &[data-size='normal']
    #{$root}--item
      padding:
        top: 1em
        bottom: 1em

  &[data-size='large']
    #{$root}--item
      padding:
        top: 1.2em
        bottom: 1.2em

  &[data-font-size='xSmall']
    #{$root}--item,
    #{$root}--caption
      font-size: $font-x-small

  &[data-font-size='small']
    #{$root}--item,
    #{$root}--caption
      font-size: $font-small

  &[data-font-size='normal']
    #{$root}--item,
    #{$root}--caption
      font-size: $font-normal

  &[data-font-size='large']
    #{$root}--item,
    #{$root}--caption
      font-size: $font-large

  &[data-font-size='xLarge']
    #{$root}--item,
    #{$root}--caption
      font-size: $font-x-large

  &[data-font-size='xxLarge']
    #{$root}--item,
    #{$root}--caption
      font-size: $font-xx-large

  +breakpoint($max-media: tablet)
    #{$root}--item
      padding:
        left: changeSizePx2Em(10, $font-small-px)
        right: changeSizePx2Em(10, $font-small-px)

    &[data-size='small']
      #{$root}--item
        padding:
          top: 0.4em
          bottom: 0.4em

    &[data-size='normal']
      #{$root}--item
        padding:
          top: 0.625em
          bottom: 0.625em

    &[data-size='large']
      #{$root}--item
        padding:
          top: 0.8em
          bottom: 0.8em

    &[data-font-size='xSmall']
      #{$root}--item,
      #{$root}--caption
        font-size: $font-xx-small

    &[data-font-size='small']
      #{$root}--item,
      #{$root}--caption
        font-size: $font-x-small

    &[data-font-size='normal']
      #{$root}--item,
      #{$root}--caption
        font-size: $font-small

    &[data-font-size='large']
      #{$root}--item,
      #{$root}--caption
        font-size: $font-normal

    &[data-font-size='xLarge']
      #{$root}--item,
      #{$root}--caption
        font-size: $font-normal

    &[data-font-size='xxLarge']
      #{$root}--item,
      #{$root}--caption
        font-size: $font-large

@keyframes shine
  12%
    left: 100%

  100%
    left: 100%
</style>
